import { DialogData, ErrorData } from "@gluckgames/commonui";
import { Services } from "appworks/services/services";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { gamevy } from "gamevy/gamevy";
import { gamevyModel } from "gamevy/gamevy-model";
import { AlertComponent } from "slotworks/components/alert/alert-component";

export enum CommonUIErrors {
    SessionReminder = "sessionReminder",
    InsufficientFunds = "insufficientFunds",
    InsufficientFundsRedirect = "insufficientFundsRedirect",
    UnsupportedCountry = "unsupportedCountry",
    AgeNotVerified = "ageNotVerified",
    SplitBalance = "splitBalance",
    TooManyOngoingGameInstances = "tooManyOngoingGameInstances",
    RequestTimeout = "requestTimeout",
    PayoutFailed = "payoutFailed",
    TokenExpired = "tokenExpired",
    InvalidSessionToken = "invalidSessionToken",
    GamingLimitReached = "gamingLimitReached",
    Unexpected = "unexpected",
    ForwardedError = "forwardedError",
    TopUpTicket = "topUpTicket",
    MaximumStakeExceeded = "maximumStakeExceeded",
    SpecificNTError = "specificNTError",
    BubbleUpError = "bubbleUpError",
    OperationNotAllowed = "operationNotAllowed",

    TechnicalError = "technicalError",
    OgsError = "ogsError",
    WagerNotFound = "wagerNotFound",
    ParameterRequired = "parameterRequired",
    GeolocationFailure = "geolocationFailure",
    JackpotWon = "jackpotWon",
    Excluded = "excluded"
}

export class CommonUIAlertComponent extends AlertComponent {
    private errorValues: string[];

    constructor() {
        super();

        const keys = Object.keys(CommonUIErrors).filter((k) => {
            return typeof CommonUIErrors[k as any] === "string";
        });
        this.errorValues = keys.map((k) => CommonUIErrors[k as any]);
    }

    public info(messageId: string, title: string = "info"): Contract<void> {
        if (this.fatal) {
            return Contract.never();
        }

        let message = this.getMessage(messageId);

        if (message === undefined || message === "" || !message) {
            message = Services.translations.get("General Error");
        }

        const data: DialogData = {
            title: "title",
            text: "text",
            buttons: [{ name: "close", caption: "", action: "close" as any }],
            props: {
                title,
                text: message,
                buttons_close: Services.translations.get("close")
            }
        };

        if (!gamevyModel.useGcm) {
            return new Contract<void>((resolve) => {
                uiFlags.set(UIFlag.PROMPT, true);

                gamevy.commonUI.showDialog(data).then((response: string) => {
                    uiFlags.set(UIFlag.PROMPT, false);
                    resolve(null);
                });
            });
        } else {
            return Contract.empty();
        }
    }

    public recover(messageID?: string): Contract<void> {
        if (gamevyModel.useGcm) {
            gamevy.commonUI.ongoingGame();
            return Contract.empty();
        } else {
            return super.recover(messageID);
        }
    }

    public freegamesInitial(amount: number): Contract<void> {
        const data: DialogData = {
            title: "title",
            text: "text",
            buttons: [{ name: "close", caption: "", action: "close" as any }],
            props: {
                title: Services.translations.get("commonui_freerounds"),
                text: `${amount} ${Services.translations.get("commonui_freerounds_awarded")}`,
                buttons_close: Services.translations.get("close")
            }
        };

        return new Contract<void>((resolve) => {
            uiFlags.set(UIFlag.PROMPT, true);
            gamevy.commonUI.showDialog(data).then(() => {
                uiFlags.set(UIFlag.PROMPT, false);
                resolve(null);
            });
        });
    }

    public freegamesComplete(totalWin: number): Contract<void> {
        const data: DialogData = {
            title: "title",
            text: "text",
            buttons: [{ name: "close", caption: "", action: "close" as any }],
            props: {
                title: Services.translations.get("commonui_freerounds_completed"),
                text: Services.translations.get("commonui_you_won_x").replace("$1", Services.currency.format(totalWin)),
                buttons_close: Services.translations.get("close")
            }
        };

        return new Contract<void>((resolve) => {
            uiFlags.set(UIFlag.PROMPT, true);
            gamevy.commonUI.showDialog(data).then(() => {
                uiFlags.set(UIFlag.PROMPT, false);
                resolve(null);
            });
        });
    }

    public error(error: string | ErrorData, fatal: boolean = false): Contract<void> {
        if (this.fatal) {
            return Contract.never();
        }

        if (fatal) {
            this.fatal = true;
            this.onFatal.dispatch();
        }

        let data: ErrorData = {code: ""};

        if (typeof (error) === "string") {
            if (this.errorValues.indexOf(error) === -1) {
                let message;
                try {
                    message = Services.translations.get(error);
                    if (message === "err.con" || message === "err.dis") {
                        data.code = CommonUIErrors.RequestTimeout;
                    } else if (message === undefined || message === "" || !message) {
                        data.code = CommonUIErrors.Unexpected;
                    } else if (fatal) {
                        data.code = CommonUIErrors.ForwardedError;
                        data.data = {
                            title: "error",
                            text: message
                        };
                    } else {
                        return this.info(message, "error");
                    }
                } catch (e) {
                    data.code = CommonUIErrors.Unexpected;
                }
            } else {
                data.code = error;
            }
        } else {
            data = error;
        }

        return new Contract<void>((resolve) => {
            uiFlags.set(UIFlag.PROMPT, true);
            gamevy.commonUI.handleError(data)
                .then((response: string) => {
                    if (this.fatal) {
                        location.reload();
                    }

                    uiFlags.set(UIFlag.PROMPT, false);
                    resolve();
                })
                .catch((reason) => {
                    // tslint:disable-next-line:no-console
                    console.error("handleError", JSON.stringify(data));
                    throw new Error("commonui.handleError error: " + JSON.stringify(reason) + " - " + JSON.stringify(data));
                });
        });
    }

    public splitBalanceMessage(message?: object): Contract<void> {
        if (message) {
            return new Contract<void>((resolve) => {
                gamevy.commonUI.handleError({
                    code: CommonUIErrors.SplitBalance,
                    data: message
                })
                .then(() => resolve())
                .catch((reason) => {
                    throw new Error("commonui.splitBalanceMessage error: " + JSON.stringify(reason) + " - " + message);
                });
            });
        } else {
            return Contract.empty();
        }
    }
}
